import React from 'react';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  CircularProgress,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext.jsx';
import { useLanguage } from '../contexts/LanguageContext.jsx';
import GoogleLoginButton from './GoogleLoginButton.jsx';

const ProtectedRoute = ({ children, requiredRole = null }) => {
  const { isAuthenticated, isLoading, user } = useAuth();
  const { t } = useLanguage();

  if (isLoading) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '60vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <CircularProgress size={48} />
          <Typography color="text.secondary">{t('common.loading')}</Typography>
        </Box>
      </Container>
    );
  }

  if (!isAuthenticated) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '60vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4,
          }}
        >
          <Card elevation={3} sx={{ width: '100%', maxWidth: 400 }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Typography 
                variant="h4" 
                component="h1" 
                sx={{ mb: 2, fontWeight: 'bold' }}
              >
                {t('auth.signInTitle')}
              </Typography>
              
              <Typography 
                variant="body1" 
                color="text.secondary" 
                sx={{ mb: 4 }}
              >
                {t('auth.signInSubtitle')}
              </Typography>
              
              <GoogleLoginButton />
              
              <Typography 
                variant="body2" 
                color="text.secondary" 
                sx={{ mt: 3 }}
              >
                By signing in, you agree to our Terms of Service and Privacy Policy
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  // Check role-based access
  if (requiredRole && user?.role !== requiredRole) {
    return (
      <Container maxWidth="sm">
        <Box
          sx={{
            minHeight: '60vh',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            py: 4,
          }}
        >
          <Card elevation={3} sx={{ width: '100%', maxWidth: 400 }}>
            <CardContent sx={{ p: 4, textAlign: 'center' }}>
              <Typography 
                variant="h4" 
                component="h1" 
                sx={{ mb: 2, fontWeight: 'bold', color: 'error.main' }}
              >
                Access Denied
              </Typography>
              
              <Typography 
                variant="body1" 
                color="text.secondary" 
                sx={{ mb: 4 }}
              >
                You don't have permission to access this page.
              </Typography>
              
              <Typography 
                variant="body2" 
                color="text.secondary"
              >
                Current role: <strong>{user?.role || 'Unknown'}</strong><br />
                Required role: <strong>{requiredRole}</strong>
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Container>
    );
  }

  return children;
};

export default ProtectedRoute;